import React, { useState } from 'react';
import { Link } from 'react-router';
import { useAuthClient } from '@/lib/authClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { BrandAside } from './brand-panel';
import { DenLogo, BRAND_FONT } from '@/components/brand/den-logo';
import { BookDemoDialog } from '@/components/auth/book-demo-dialog';

type SignInFormValues = {
  email: string;
  password: string;
};

type SignInFormErrors = Partial<Record<keyof SignInFormValues, string>>;

/**
 * The sign-in screen: a two-column layout with the branded graphic aside on the
 * left (desktop only, via {@link BrandAside}) and the white email/password form
 * on the right. On mobile the aside is hidden and an inline Den logo is shown at
 * the top of the form instead.
 *
 * Validation mirrors appliedlabs.ai/login: the submit button is never disabled
 * for invalid input (only while a request is in flight); clicking runs
 * {@link validate} and surfaces field-level messages. The top-right "Contact
 * Sales" button opens the {@link BookDemoDialog}, whose open state this
 * component owns.
 */
export default function SignInPage() {
  const { signIn } = useAuthClient();

  const [form, setForm] = useState<SignInFormValues>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<SignInFormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // "Book a demo" dialog, opened from the top-right "Contact Sales" button.
  const [bookDemoOpen, setBookDemoOpen] = useState(false);

  const handleInputChange = (field: keyof SignInFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _omit, ...rest } = prev;
        return rest;
      });
    }
    if (generalError) setGeneralError('');
  };

  const validate = (): boolean => {
    const newErrors: SignInFormErrors = {};
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      newErrors.email = 'Please enter a valid email address';

    if (!form.password) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');
    if (!validate()) return;

    setIsLoading(true);
    const data = await signIn.emailAndPassword({
      email: form.email.trim(),
      password: form.password,
    });
    if (data.error) {
      setGeneralError(data.error.message ?? 'Unknown error');
    }
    setIsLoading(false);
  };

  return (
    <div
      className="grid min-h-screen w-full grid-cols-1 bg-white lg:grid-cols-2"
      style={{ fontFamily: BRAND_FONT }}
    >
      <BrandAside className="hidden lg:flex" />

      {/* Right column: a plain white panel, full-bleed to every edge. */}
      <div className="relative flex items-center justify-center bg-white px-6 py-12 sm:px-10">
        {/* Secondary action, top-right — outlined "Contact Sales" button
            (mirrors Applied Labs' nav); opens the book-a-demo dialog. */}
        <div className="absolute right-6 top-6 sm:right-10 sm:top-8">
          <Button
            type="button"
            variant="outline"
            className="h-9 rounded-[6px] border-[#e4e4e7] bg-white px-4 text-sm font-medium text-[#09090b] shadow-none hover:bg-[#f4f4f5] hover:text-[#09090b]"
            onClick={() => setBookDemoOpen(true)}
          >
            Contact Sales
          </Button>
        </div>

        <div className="w-full max-w-[440px]">
          {/* Logo shown inline only when the brand aside is hidden (mobile);
              links out to the marketing site with a subtle hover affordance. */}
          <a
            href="https://onchainden.com"
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Den — visit onchainden.com"
            className="mb-10 inline-block transition-opacity duration-200 hover:opacity-70 lg:hidden"
          >
            <DenLogo className="h-8 w-auto" />
          </a>

          <h1 className="text-4xl font-normal leading-tight tracking-tight text-[#09090b]">
            Welcome back
          </h1>

          {/* noValidate: skip the browser's native field validation so our own
              validate() messages show on submit (mirrors Applied Labs), rather
              than a native tooltip pre-empting them for an invalid email. */}
          <form onSubmit={handleSubmit} className="mt-8" noValidate>
            <div className="space-y-5">
              <div>
                <Label
                  htmlFor="email"
                  className="mb-2 block text-xs font-semibold text-[#71717a]"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`h-[52px] rounded-lg border-2 bg-white px-4 text-sm text-[#09090b] placeholder:text-[#a1a1aa] focus-visible:ring-0 ${errors.email ? 'border-destructive focus-visible:border-destructive' : 'border-[#18181b] focus-visible:border-[#09090b]'}`}
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="mt-1.5 text-sm text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="password"
                  className="mb-2 block text-xs font-semibold text-[#71717a]"
                >
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={form.password}
                    onChange={(e) =>
                      handleInputChange('password', e.target.value)
                    }
                    className={`h-[52px] rounded-lg border-2 bg-white px-4 pr-11 text-sm text-[#09090b] placeholder:text-[#a1a1aa] focus-visible:ring-0 ${errors.password ? 'border-destructive focus-visible:border-destructive' : 'border-[#18181b] focus-visible:border-[#09090b]'}`}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a1a1aa] transition-colors hover:text-[#09090b]"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-sm text-destructive">
                    {errors.password}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3">
              <Link
                to="/forgot-password"
                className="text-xs font-normal text-[#71717a] hover:text-[#09090b]"
              >
                Forgot password?
              </Link>
            </div>

            {generalError && (
              <p className="mt-4 text-sm text-destructive">{generalError}</p>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="mt-6 h-12 w-full rounded-md text-sm font-medium"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>

            {/* Consent line, styled to match Applied Labs' login page. */}
            <p className="mt-4 text-center text-xs font-normal text-[#71717a]">
              By signing in, you agree to our{' '}
              <a
                href="https://onchainden.com/terms"
                target="_blank"
                rel="noreferrer noopener"
                className="font-normal text-[#71717a] underline underline-offset-2 hover:text-[#09090b]"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href="https://onchainden.com/privacy"
                target="_blank"
                rel="noreferrer noopener"
                className="font-normal text-[#71717a] underline underline-offset-2 hover:text-[#09090b]"
              >
                Privacy Policy
              </a>
              .
            </p>
          </form>
        </div>
      </div>

      <BookDemoDialog open={bookDemoOpen} onOpenChange={setBookDemoOpen} />
    </div>
  );
}
